'use client'

import { cn } from '@/lib/utils'

type Direction = 'right' | 'left' | 'bottom' | 'top'

const AXIS: Record<Direction, string> = {
  right: 'to right',
  left: 'to left',
  bottom: 'to bottom',
  top: 'to top',
}

interface ProgressiveBlurProps {
  /** Where the blur ramps *towards*. `right` = sharp on the left, blurred on the right. */
  direction?: Direction
  /** Stacked backdrop layers. More = smoother ramp, more compositing cost. */
  layers?: number
  /** Radius of the first layer in px; each subsequent layer doubles it. */
  baseBlur?: number
  /**
   * Percentage along the axis where the ramp begins — everything before it stays
   * completely untouched. This is the main dial for "how much of the image stays
   * sharp"; prefer raising it over lowering `baseBlur`, which just makes the
   * whole ramp mushy instead of shortening it.
   */
  start?: number
  className?: string
}

/**
 * Apple-style progressive (gradient) blur.
 *
 * A single `blur()` behind a fading mask only ramps *opacity* — the sharpness
 * stays constant the whole way across, which is why it reads as a dissolve
 * rather than depth. A real progressive blur needs a stack: each layer blurs the
 * backdrop harder than the one beneath it and is masked to its own narrow band,
 * so the effective radius climbs smoothly along the axis.
 *
 * Two details make it seamless:
 *   - Bands overlap. Each spans three steps and is only fully opaque across the
 *     middle one, so neighbouring layers cross-fade instead of showing a seam.
 *   - Radii double per layer, because each layer re-blurs the already-blurred
 *     result underneath it; linear radii would bunch up at the sharp end.
 *
 * Note: an ancestor with `mask`, `filter`, or `opacity < 1` becomes a backdrop
 * root, which conveniently scopes the blur to that subtree's own content (e.g.
 * the image) rather than the whole page behind it.
 */
export function ProgressiveBlur({
  direction = 'right',
  layers = 4,
  baseBlur = 0.45,
  start = 0,
  className,
}: ProgressiveBlurProps) {
  const axis = AXIS[direction]
  // The ramp is packed into whatever is left after `start`, so raising `start`
  // shortens the ramp rather than stretching a weaker one across the whole axis.
  const step = (100 - start) / (layers + 1)
  const at = (n: number) => +(start + n * step).toFixed(2)

  return (
    <div
      aria-hidden
      className={cn('pointer-events-none absolute inset-0', className)}
    >
      {Array.from({ length: layers }, (_, i) => {
        const mask = `linear-gradient(${axis}, transparent ${at(i)}%, #000 ${at(
          i + 1
        )}%, #000 ${at(i + 2)}%, transparent ${at(i + 3)}%)`
        const radius = baseBlur * 2 ** i

        return (
          <div
            key={i}
            className="absolute inset-0"
            style={{
              backdropFilter: `blur(${radius}px)`,
              WebkitBackdropFilter: `blur(${radius}px)`,
              maskImage: mask,
              WebkitMaskImage: mask,
            }}
          />
        )
      })}
    </div>
  )
}
